import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Gift, Coins, Trophy, Crown } from "lucide-react";
import { toast } from "sonner";

function RewardForm({ initial, onSaved }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    description: initial?.description || "",
    cost_points: initial?.cost_points || 100,
    color: initial?.color || "#1B2E7A",
    stock: initial?.stock ?? -1,
    visible: initial?.visible ?? true,
  });
  const isEdit = !!initial;

  const submit = async () => {
    if (!form.title || !form.cost_points) {
      toast.error("Título y puntos requeridos");
      return;
    }
    try {
      if (isEdit) {
        await api.patch(`/admin/rewards/${initial.id}`, { ...form, cost_points: Number(form.cost_points), stock: Number(form.stock) });
      } else {
        await api.post("/admin/rewards", { ...form, cost_points: Number(form.cost_points), stock: Number(form.stock) });
      }
      toast.success(isEdit ? "Premio actualizado" : "Premio creado");
      onSaved();
    } catch {
      toast.error("Error al guardar");
    }
  };

  return (
    <DialogContent className="bg-white max-w-md">
      <DialogHeader>
        <DialogTitle className="font-heading">{isEdit ? "Editar premio" : "Nuevo premio"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <Input placeholder="Título del premio" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="reward-title-input" />
        <Textarea placeholder="Descripción" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" placeholder="Puntos" value={form.cost_points} onChange={(e) => setForm({ ...form, cost_points: e.target.value })} data-testid="reward-cost-input" />
          <Input type="number" placeholder="Stock (-1 = ilimitado)" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
        </div>
        <div className="flex items-center gap-2">
          <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="max-w-[120px]" />
          <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-12 cursor-pointer" />
          <span className="text-xs text-[#737373]">Color</span>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} className="accent-[#1B2E7A]" />
          Visible para los vendedores
        </label>
      </div>
      <DialogFooter>
        <Button onClick={submit} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="save-reward">
          Guardar
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

const STATUS_LABELS = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
  delivered: "Entregado",
};

export default function AdminRewards() {
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, rd, lb] = await Promise.all([
        api.get("/admin/rewards"),
        api.get("/admin/redemptions"),
        api.get("/admin/points"),
      ]);
      setRewards(r.data);
      setRedemptions(rd.data);
      setLeaderboard(lb.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar premio?")) return;
    await api.delete(`/admin/rewards/${id}`);
    toast.success("Premio eliminado");
    load();
  };

  const decide = async (rid, status) => {
    const note = status === "rejected" ? (window.prompt("Motivo del rechazo (opcional)") || "") : "";
    try {
      await api.patch(`/admin/redemptions/${rid}`, { status, admin_note: note });
      toast.success("Canje actualizado");
      load();
    } catch {
      toast.error("Error");
    }
  };

  if (loading) return <div className="text-[#374151]">Cargando...</div>;

  const pendingCount = redemptions.filter((r) => r.status === "pending").length;

  return (
    <div data-testid="admin-rewards-page">
      <div className="mb-10 fade-up">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2 inline-flex items-center gap-1.5">
          <Gift className="h-3.5 w-3.5" /> Solo administradores
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-3">
          Reconocimiento y Premios.
        </h1>
        <p className="text-[#374151] max-w-2xl leading-relaxed">
          Gestiona el catálogo de premios, aprueba canjes y revisa el ranking de puntos del equipo.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <div className="bg-white border border-[#E5E7EB] rounded-md p-5">
          <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280]">Premios activos</p>
          <p className="font-heading text-3xl text-[#1A1A1A]">{rewards.filter((r) => r.visible).length}</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-md p-5">
          <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280]">Canjes pendientes</p>
          <p className="font-heading text-3xl text-[#DC2626]">{pendingCount}</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-md p-5">
          <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280]">Vendedores con puntos</p>
          <p className="font-heading text-3xl text-[#1B2E7A]">{leaderboard.length}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl text-[#1A1A1A]">Catálogo de premios</h2>
            <Dialog open={open && !editing} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="create-reward-button">
                  <Plus className="h-4 w-4 mr-2" /> Nuevo premio
                </Button>
              </DialogTrigger>
              {open && !editing && (
                <RewardForm initial={null} onSaved={() => { setOpen(false); load(); }} />
              )}
            </Dialog>
          </div>
          <div className="space-y-2">
            {rewards.map((r) => (
              <div key={r.id} className="bg-white border border-[#E5E7EB] rounded-md p-4 flex items-center gap-3" data-testid={`admin-reward-${r.id}`}>
                <div className="h-10 w-10 rounded-md flex items-center justify-center text-white" style={{ background: r.color || "#1B2E7A" }}>
                  <Gift className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A1A1A]">{r.title}</p>
                  <p className="text-xs text-[#737373]">
                    {r.cost_points} pts · {r.stock === -1 ? "Ilimitado" : `${r.stock} en stock`} · {r.visible ? "Visible" : "Oculto"}
                  </p>
                </div>
                <Dialog open={editing?.id === r.id} onOpenChange={(o) => setEditing(o ? r : null)}>
                  <DialogTrigger asChild>
                    <button className="text-[#1B2E7A] hover:bg-[#F3F4F6] p-1.5 rounded-md" data-testid={`edit-reward-${r.id}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                  </DialogTrigger>
                  {editing?.id === r.id && (
                    <RewardForm initial={r} onSaved={() => { setEditing(null); load(); }} />
                  )}
                </Dialog>
                <button onClick={() => remove(r.id)} className="text-[#DC2626] hover:bg-[#DC2626]/10 p-1.5 rounded-md">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-5 w-5 text-[#EAB308]" strokeWidth={1.5} />
            <h2 className="font-heading text-xl text-[#1A1A1A]">Top vendedores</h2>
          </div>
          {leaderboard.length === 0 ? (
            <p className="text-sm text-[#737373]">Aún no hay puntos acumulados.</p>
          ) : (
            <div className="space-y-2">
              {leaderboard.slice(0, 10).map((row, i) => (
                <div key={row.user.user_id} className="bg-white border border-[#E5E7EB] rounded-md p-3 flex items-center gap-3" data-testid={`leaderboard-${row.user.user_id}`}>
                  <span className={`h-8 w-8 rounded-md flex items-center justify-center font-heading text-sm ${i < 3 ? "bg-[#EAB308] text-white" : "bg-[#F3F4F6] text-[#374151]"}`}>
                    {i === 0 ? <Crown className="h-4 w-4" /> : i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A1A1A] truncate">{row.user.name}</p>
                    <p className="text-xs text-[#737373]">{row.user.role === "admin" ? "Admin" : "Vendedor"}</p>
                  </div>
                  <span className="font-heading text-lg text-[#1B2E7A] inline-flex items-center gap-1">
                    <Coins className="h-3.5 w-3.5 text-[#EAB308]" />
                    {row.total.toLocaleString("es-MX")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <h2 className="font-heading text-xl text-[#1A1A1A] mb-4">Canjes ({redemptions.length})</h2>
        <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FFFFFF] text-[#737373]">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Vendedor</th>
                <th className="text-left px-5 py-3 font-medium">Premio</th>
                <th className="text-left px-5 py-3 font-medium">Puntos</th>
                <th className="text-left px-5 py-3 font-medium">Fecha</th>
                <th className="text-left px-5 py-3 font-medium">Estado</th>
                <th className="text-right px-5 py-3 font-medium">Acción</th>
              </tr>
            </thead>
            <tbody>
              {redemptions.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-[#737373]">Aún no hay canjes.</td>
                </tr>
              )}
              {redemptions.map((rd) => (
                <tr key={rd.id} className="border-t border-[#E5E7EB]" data-testid={`admin-redemption-${rd.id}`}>
                  <td className="px-5 py-3 text-[#1A1A1A]">{rd.user_name}</td>
                  <td className="px-5 py-3 text-[#374151]">{rd.reward_title}</td>
                  <td className="px-5 py-3 text-[#374151]">{rd.cost_points}</td>
                  <td className="px-5 py-3 text-[#737373] text-xs">
                    {new Date(rd.created_at).toLocaleDateString("es-MX")}
                  </td>
                  <td className="px-5 py-3 text-xs">{STATUS_LABELS[rd.status]}</td>
                  <td className="px-5 py-3 text-right">
                    {rd.status === "pending" && (
                      <>
                        <button onClick={() => decide(rd.id, "approved")} className="text-[#1B2E7A] text-xs hover:underline mr-2">Aprobar</button>
                        <button onClick={() => decide(rd.id, "rejected")} className="text-[#DC2626] text-xs hover:underline">Rechazar</button>
                      </>
                    )}
                    {rd.status === "approved" && (
                      <button onClick={() => decide(rd.id, "delivered")} className="text-[#1B2E7A] text-xs hover:underline">
                        Marcar entregado
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
